import { fetchPost } from "app/utils/request";
import errorLogger from "app/utils/errorLogger";

interface SyncContactUser {
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * Registers the current athlete as a contact of the coach on the edufii API so
 * the backend syncs them into the scheduling service (contact + space). This is
 * the shared implementation behind both the booking flow
 * (PublicCalendar/Confirmation) and program registration.
 *
 * Sends `sync_now: true` so the sync happens inline. Rejects when the request
 * fails, letting the caller block the surrounding action. No-op (resolves)
 * when the user is missing; a missing coach external id is reported to Bugsnag,
 * since silently dropping the sync reproduces the very symptom this addresses
 * (a program with no coach leaves the athlete unsynced).
 */
export const syncAthleteContact = async (
  user: SyncContactUser | null | undefined,
  coachExternalId: string | undefined,
): Promise<void> => {
  if (!user) {
    return;
  }

  if (!coachExternalId) {
    errorLogger("syncAthleteContact skipped: missing coach external id", {
      severity: "warning",
      metaData: { contact: { email: user.email } },
    });
    return;
  }

  await fetchPost("/contacts", {
    contact: {
      first_name: user.firstName,
      last_name: user.lastName,
      email: user.email,
      coach_id: coachExternalId,
      role: "athlete",
      sync_now: true,
    },
  });
};
